import React, { useState } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Chip, 
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Paper,
  Card,
  CardContent,
  CardActions,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Add as AddIcon, 
  Refresh as RefreshIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  AutoAwesome as AutoAwesomeIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import useMeetingsWithData from '../hooks/useMeetingsWithData';

/**
 * Exemple d'utilisation du hook useMeetingsWithData
 * Montre comment simplifier la gestion des meetings avec leurs transcriptions et résumés
 */
const MeetingsListPageExample = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { addSystemNotification } = useNotifications();
  
  // État local simple
  const [searchTerm, setSearchTerm] = useState('');
  
  // Utilisation du hook unifié - TOUTE la complexité est cachée ici
  const {
    meetings,           // Meetings enrichis avec transcription + summary
    loading,            // État de chargement global
    error,             // Erreur globale
    stats,             // Statistiques calculées automatiquement
    getMeetingWithData, // Fonction pour récupérer un meeting spécifique depuis le cache
    refreshAdminStatus, // Fonction pour rafraîchir les droits admin
    reload,            // Fonction pour forcer un rechargement
    isInitialized,     // État d'initialisation du service
    isAdmin            // Statut admin de l'utilisateur
  } = useMeetingsWithData();

  // Filtrage simple côté client
  const filteredMeetings = meetings.filter(meeting => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      meeting.title?.toLowerCase().includes(searchLower) ||
      meeting.context?.toLowerCase().includes(searchLower)
    );
  });

  // Fonctions de gestion des actions
  const handleViewMeeting = (meetingId) => {
    navigate(`/meetings/${meetingId}`);
  };

  const handleEditMeeting = (meetingId) => {
    // Logique d'édition
  };

  const handleRefresh = async () => {
    try {
      await reload();
    } catch (err) {
      addSystemNotification('Erreur lors du rechargement:', 'error');
    }
  };

  // Composant pour une carte de meeting simplifiée
  const MeetingCardExample = ({ meeting }) => {
    const getStatusChip = () => {
      if (meeting.hasSummary) {
        return (
          <Chip 
            icon={<AutoAwesomeIcon />}
            label="Résumé disponible" 
            size="small" 
            color="warning"
            variant="outlined"
          />
        );
      }
      if (meeting.hasTranscription) {
        return (
          <Chip 
            icon={<CheckCircleIcon />}
            label="Transcription terminée" 
            size="small" 
            color="success"
            variant="outlined"
          />
        );
      }
      return (
        <Chip 
          icon={<AccessTimeIcon />}
          label="En attente" 
          size="small" 
          color="default"
          variant="outlined"
        />
      );
    };

    return (
      <Card 
        elevation={1}
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            elevation: 3,
            transform: 'translateY(-2px)'
          }
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="h6" component="h2" sx={{ flexGrow: 1, mr: 1 }}>
              {meeting.title}
            </Typography>
            {meeting.isConfidential && (
              <Tooltip title="Réunion confidentielle">
                <SecurityIcon color="error" fontSize="small" />
              </Tooltip>
            )}
          </Box>
          
          {meeting.context && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {meeting.context}
            </Typography>
          )}
          
          <Box sx={{ mb: 2 }}>
            {getStatusChip()}
          </Box>
          
          <Typography variant="caption" color="text.secondary">
            {meeting.date ? format(new Date(meeting.date), "d MMMM yyyy", { locale: fr }) : 'Date non disponible'}
          </Typography>
        </CardContent>
        
        <CardActions>
          <Tooltip title="Voir la réunion">
            <IconButton size="small" onClick={() => handleViewMeeting(meeting.id)}>
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Modifier">
            <IconButton size="small" onClick={() => handleEditMeeting(meeting.id)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
        </CardActions>
      </Card>
    );
  };

  // Interface de chargement
  if (loading && !isInitialized) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Initialisation...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', py: 4, px: 2 }}>
      {/* En-tête avec statistiques */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1">
            Mes Réunions {isAdmin && '(Admin)'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Actualiser">
              <IconButton onClick={handleRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => console.log('Nouvelle réunion')}
            >
              Nouvelle réunion
            </Button>
          </Box>
        </Box>

        {/* Panneau de statistiques */}
        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f8faff' }}>
          <Typography variant="h6" gutterBottom>
            📊 Statistiques
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="h4" color="primary">{stats.totalMeetings}</Typography>
              <Typography variant="caption">Total réunions</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="h4" color="success.main">{stats.meetingsWithTranscription}</Typography>
              <Typography variant="caption">Transcrites</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="h4" color="warning.main">{stats.meetingsWithSummary}</Typography>
              <Typography variant="caption">Avec résumé</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="h4" color="info.main">
                {stats.pendingTranscriptions + stats.pendingSummaries}
              </Typography>
              <Typography variant="caption">En cours</Typography>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Barre de recherche */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Rechercher dans vos réunions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 600 }}
        />
      </Box>

      {/* Gestion des erreurs */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Liste des réunions */}
      {loading && isInitialized ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredMeetings.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {searchTerm ? 'Aucune réunion trouvée' : 'Aucune réunion disponible'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchTerm 
              ? 'Essayez avec un autre terme de recherche'
              : 'Commencez par créer votre première réunion'
            }
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredMeetings.map((meeting) => (
            <Grid item xs={12} sm={6} md={4} key={meeting.id}>
              <MeetingCardExample meeting={meeting} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Informations de debug en mode développement */}
      {process.env.NODE_ENV === 'development' && (
        <Paper sx={{ p: 2, mt: 4, backgroundColor: '#f5f5f5' }}>
          <Typography variant="subtitle2" gutterBottom>
            🔧 Debug Info
          </Typography>
          <Typography variant="caption" component="div">
            • Service initialisé: {isInitialized ? '✅' : '❌'}
          </Typography>
          <Typography variant="caption" component="div">
            • Utilisateur admin: {isAdmin ? '✅' : '❌'}
          </Typography>
          <Typography variant="caption" component="div">
            • Meetings en cache: {meetings.length}
          </Typography>
          <Typography variant="caption" component="div">
            • Erreur: {error || 'Aucune'}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default MeetingsListPageExample; 